import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type {
  Agency,
  Bank,
  CardBrand,
  Hotel,
  PageResponse,
  User,
  UserRole,
} from "@/api/types";
import { useAuth } from "@/app/auth";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Empty, PageLead, Panel } from "@/components/Page";
import { Pagination } from "@/components/Pagination";
import { useToast } from "@/components/Toast";

type Resource = "users" | "hotels" | "banks" | "cards" | "agencies";

interface ConfigurationSummary {
  users: number;
  hotels: number;
  banks: number;
  cards: number;
  agencies: number;
}

const labels: Record<
  Resource,
  { title: string; description: string; path: string }
> = {
  users: {
    title: "Usuarios",
    description: "Accesos, roles y estado de quienes operan el sistema.",
    path: "usuarios",
  },
  hotels: {
    title: "Hoteles",
    description: "Identidad legal y operativa de cada establecimiento.",
    path: "hoteles",
  },
  banks: {
    title: "Bancos",
    description: "Entidades bancarias disponibles para cargas y conciliaciones.",
    path: "bancos",
  },
  cards: {
    title: "Tarjetas",
    description: "Marcas disponibles para las liquidaciones de tarjetas.",
    path: "tarjetas",
  },
  agencies: {
    title: "Agencias",
    description: "Agencias, OTAs, aliases y perfiles para cobranzas.",
    path: "agencias",
  },
};

function ResourceIcon({ resource }: { resource: Resource }) {
  const paths: Record<Resource, ReactNode> = {
    users: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 19.5v-1.3A5.5 5.5 0 0 1 9 12.7a5.5 5.5 0 0 1 5.5 5.5v1.3M15.5 5.5a3 3 0 0 1 0 5.8M17 13.3a5 5 0 0 1 3.5 4.8v1.4" />
      </>
    ),
    hotels: (
      <>
        <path d="M5 21V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v17M3 21h18" />
        <path d="M8 7h2M14 7h2M8 11h2M14 11h2M8 15h2M14 15h2M10 21v-3h4v3" />
      </>
    ),
    banks: (
      <>
        <path d="m3 9 9-5 9 5M4 20h16M6 9v8M10 9v8M14 9v8M18 9v8M3 21h18" />
      </>
    ),
    cards: (
      <>
        <rect x="2.5" y="5" width="19" height="14" rx="2" />
        <path d="M2.5 9h19M6 15h4" />
      </>
    ),
    agencies: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3.5 9h17M3.5 15h10M12 3a14 14 0 0 1 3 9M12 3a14 14 0 0 0-3 9M9 21a14 14 0 0 1-1.1-3.2" />
        <path d="M18 14.5a3 3 0 0 0-3 3c0 2.2 3 4.5 3 4.5s3-2.3 3-4.5a3 3 0 0 0-3-3Z" />
      </>
    ),
  };
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths[resource]}
    </svg>
  );
}

function ManagementOnly({ children }: PropsWithChildren) {
  const { user } = useAuth();
  if (user?.role !== "ADMIN" && user?.role !== "SUPERVISOR") {
    return (
      <div className="empty-state">
        <h1>Configuración restringida</h1>
        <p>Se requiere el rol Administrador o Supervisor.</p>
      </div>
    );
  }
  return children;
}

export function SettingsPage() {
  const summary = useQuery({
    queryKey: ["configuration-summary"],
    queryFn: (): Promise<ConfigurationSummary> => responseData(apiClient.GET("/api/v1/administration/summary")),
  });
  const resources: Resource[] = [
    "users",
    "hotels",
    "banks",
    "cards",
    "agencies",
  ];

  return (
    <ManagementOnly>
      <Breadcrumb items={[{ label: "Configuración" }]} />
      <PageLead
        eyebrow="Administración"
        title="Configuración"
        description="Entrá al recurso que necesitás administrar."
      />
      <section className="settings-catalog">
        {resources.map((resource) => {
          const label = labels[resource];
          return (
            <Link
              className="settings-card"
              to={`/configuracion/${label.path}`}
              key={resource}
            >
              <span className="settings-card__mark" aria-hidden="true">
                <ResourceIcon resource={resource} />
              </span>
              <div>
                <small>Recurso</small>
                <h2>{label.title}</h2>
                <p>{label.description}</p>
              </div>
              <strong>
                {summary.data?.[resource] ?? "—"}
                <small> registros</small>
              </strong>
              <i>Administrar →</i>
            </Link>
          );
        })}
      </section>
    </ManagementOnly>
  );
}

function ResourceShell({
  resource,
  children,
}: {
  resource: Resource;
  children: ReactNode;
}) {
  const label = labels[resource];
  return (
    <ManagementOnly>
      <Breadcrumb
        items={[
          { label: "Configuración", to: "/configuracion" },
          { label: label.title },
        ]}
      />
      <PageLead
        eyebrow="Configuración"
        title={label.title}
        description={label.description}
      />
      {children}
    </ManagementOnly>
  );
}

function useListState() {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page") ?? 1));
  const q = params.get("q") ?? "";
  const [search, setSearch] = useState(q);

  const apply = () => {
    const next = new URLSearchParams(params);
    search ? next.set("q", search) : next.delete("q");
    next.set("page", "1");
    setParams(next);
  };

  const setPage = (value: number) => {
    const next = new URLSearchParams(params);
    next.set("page", String(value));
    setParams(next);
  };

  return { page, q, search, setSearch, apply, setPage };
}

interface ResourceTableColumn<T> {
  key: string;
  label: string;
  className?: string;
  render(item: T): ReactNode;
}

function ResourceTable<T extends { id: string }>({
  title,
  data,
  columns,
  onEdit,
  onCreate,
  createLabel,
  getRowLabel,
  search,
  setSearch,
  apply,
  onPage,
}: {
  title: string;
  data?: PageResponse<T>;
  columns: Array<ResourceTableColumn<T>>;
  onEdit(item: T): void;
  onCreate(): void;
  createLabel: string;
  getRowLabel(item: T): string;
  search: string;
  setSearch(value: string): void;
  apply(): void;
  onPage(page: number): void;
}) {
  return (
    <Panel title={title} meta={`${data?.meta.total ?? 0} registros`}>
      <form
        className="filter-bar settings-resource-toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          apply();
        }}
      >
        <input
          aria-label={`Buscar ${title.toLowerCase()}`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={`Buscar ${title.toLowerCase()}`}
        />
        <button className="button button--small">Buscar</button>
        <button
          type="button"
          className="button button--primary button--small"
          onClick={onCreate}
        >
          {createLabel}
        </button>
      </form>
      <div className="table-wrap settings-table-wrap">
        <table className="settings-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={column.className}>
                  {column.label}
                </th>
              ))}
              <th className="settings-table__actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.map((item) => (
              <tr key={item.id}>
                {columns.map((column) => (
                  <td key={column.key} className={column.className}>
                    {column.render(item)}
                  </td>
                ))}
                <td className="settings-table__actions">
                  <button
                    type="button"
                    className="button button--small settings-table__edit"
                    aria-label={`Editar ${getRowLabel(item)}`}
                    onClick={() => onEdit(item)}
                  >
                    Editar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!data?.items.length && (
        <Empty>No hay registros para estos filtros.</Empty>
      )}
      <Pagination meta={data?.meta} onPageChange={onPage} />
    </Panel>
  );
}

function ResourceStatus({
  active,
  feminine = false,
}: {
  active: boolean;
  feminine?: boolean;
}) {
  return (
    <span className={`status status--${active ? "success" : "warning"}`}>
      {active ? (feminine ? "Activa" : "Activo") : feminine ? "Inactiva" : "Inactivo"}
    </span>
  );
}

function SettingsCrudModal({
  eyebrow,
  title,
  pending,
  onClose,
  children,
}: PropsWithChildren<{
  eyebrow: string;
  title: string;
  pending: boolean;
  onClose(): void;
}>) {
  const titleId = useId();
  const dialog = useRef<HTMLElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const closeHandler = useRef(onClose);
  const pendingState = useRef(pending);
  closeHandler.current = onClose;
  pendingState.current = pending;

  useEffect(() => {
    const returnFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => {
      const initialField = dialog.current?.querySelector<HTMLElement>(
        "input:not(:disabled), select:not(:disabled), textarea:not(:disabled)",
      );
      (initialField ?? closeButton.current)?.focus();
    });
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pendingState.current) {
        closeHandler.current();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialog.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]',
        ) ?? [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keydown);
      returnFocus?.focus();
    };
  }, []);

  return (
    <div
      className="modal-backdrop settings-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !pending) onClose();
      }}
    >
      <section
        ref={dialog}
        className="import-modal settings-crud-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header>
          <div>
            <small>{eyebrow}</small>
            <h2 id={titleId}>{title}</h2>
          </div>
          <button
            ref={closeButton}
            type="button"
            className="modal-close"
            aria-label="Cerrar modal"
            disabled={pending}
            onClick={onClose}
          >
            ×
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function FormActions({
  pending,
  editing,
  noun,
  onCancel,
}: {
  pending: boolean;
  editing: boolean;
  noun: string;
  onCancel(): void;
}) {
  return (
    <div className="settings-modal-actions">
      <button
        type="button"
        className="button"
        disabled={pending}
        onClick={onCancel}
      >
        Cancelar
      </button>
      <SaveButton pending={pending} editing={editing} noun={noun} />
    </div>
  );
}

function SaveButton({
  pending,
  editing,
  noun,
}: {
  pending: boolean;
  editing: boolean;
  noun: string;
}) {
  return (
    <button className="button button--primary" disabled={pending}>
      {pending ? "Guardando…" : editing ? "Guardar cambios" : `Crear ${noun}`}
    </button>
  );
}

function ActiveField({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange(value: boolean): void;
  label: string;
}) {
  return (
    <label className="check-field">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />{" "}
      {label}
    </label>
  );
}

export function SettingsUsersPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const list = useListState();
  const empty = {
    id: "",
    username: "",
    displayName: "",
    role: "OPERATOR" as UserRole,
    password: "",
    active: true,
  };
  const [current, setCurrent] = useState(empty);
  const [modalOpen, setModalOpen] = useState(false);
  const users = useQuery({
    queryKey: ["settings-users", list.page, list.q],
    queryFn: (): Promise<PageResponse<User>> => responseData(apiClient.GET("/api/v1/identity/users", {
      params: { query: { page: list.page, pageSize: 25, q: list.q, active: "all" } },
    })),
  });
  const save = useMutation({
    mutationFn: () => responseData(apiClient.POST("/api/v1/identity/users", { body: current })),
    onSuccess: async () => {
      toast.success("Usuario guardado.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["settings-users"] }),
        queryClient.invalidateQueries({ queryKey: ["configuration-summary"] }),
      ]);
      setModalOpen(false);
      setCurrent(empty);
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo guardar el usuario." }),
  });

  return (
    <ResourceShell resource="users">
      <ResourceTable
        title="Usuarios"
        data={users.data}
        search={list.search}
        setSearch={list.setSearch}
        apply={list.apply}
        onPage={list.setPage}
        createLabel="Nuevo usuario"
        getRowLabel={(item) => item.displayName}
        onCreate={() => {
          setCurrent(empty);
          setModalOpen(true);
        }}
        onEdit={(item) => {
            setCurrent({
              id: item.id,
              username: item.username,
              displayName: item.displayName,
              role: item.role,
              password: "",
              active: item.active ?? true,
            });
            setModalOpen(true);
          }}
        columns={[
          {
            key: "name",
            label: "Nombre",
            render: (item) => <strong>{item.displayName}</strong>,
          },
          {
            key: "username",
            label: "Usuario",
            className: "mono",
            render: (item) => item.username,
          },
          { key: "role", label: "Rol", render: (item) => item.role },
          {
            key: "status",
            label: "Estado",
            render: (item) => <ResourceStatus active={item.active !== false} />,
          },
        ]}
      />
      {modalOpen && (
        <SettingsCrudModal
          eyebrow="Configuración · Usuarios"
          title={current.id ? "Editar usuario" : "Crear usuario"}
          pending={save.isPending}
          onClose={() => setModalOpen(false)}
        >
          <form
            className="stack-form"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <div className="form-row">
              <label>
                Usuario
                <input
                  required
                  value={current.username}
                  onChange={(event) =>
                    setCurrent({ ...current, username: event.target.value })
                  }
                />
              </label>
              <label>
                Nombre
                <input
                  required
                  value={current.displayName}
                  onChange={(event) =>
                    setCurrent({ ...current, displayName: event.target.value })
                  }
                />
              </label>
            </div>
            <label>
              Rol
              <select
                value={current.role}
                onChange={(event) =>
                  setCurrent({
                    ...current,
                    role: event.target.value as UserRole,
                  })
                }
              >
                <option value="OPERATOR">Operador</option>
                <option value="SUPERVISOR">Supervisor</option>
                <option value="ADMIN">Administrador</option>
              </select>
            </label>
            <label>
              Contraseña {current.id && "(sólo para cambiarla)"}
              <input
                type="password"
                autoComplete="new-password"
                required={!current.id}
                value={current.password}
                onChange={(event) =>
                  setCurrent({ ...current, password: event.target.value })
                }
              />
            </label>
            <ActiveField
              checked={current.active}
              onChange={(active) => setCurrent({ ...current, active })}
              label="Usuario activo"
            />
            <FormActions
              pending={save.isPending}
              editing={Boolean(current.id)}
              noun="usuario"
              onCancel={() => setModalOpen(false)}
            />
          </form>
        </SettingsCrudModal>
      )}
    </ResourceShell>
  );
}

export function SettingsHotelsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const list = useListState();
  const empty = {
    id: "",
    code: "",
    name: "",
    taxId: "",
    legalName: "",
    logo: "",
    active: true,
  };
  const [hotel, setHotel] = useState(empty);
  const [modalOpen, setModalOpen] = useState(false);
  const hotels = useQuery({
    queryKey: ["settings-hotels", list.page, list.q],
    queryFn: (): Promise<PageResponse<Hotel>> => responseData(apiClient.GET("/api/v1/hotels", {
      params: { query: { page: list.page, pageSize: 25, q: list.q, active: "all" } },
    })),
  });
  const save = useMutation({
    mutationFn: () => responseData(apiClient.POST("/api/v1/hotels", { body: hotel })),
    onSuccess: async () => {
      toast.success("Hotel guardado.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["settings-hotels"] }),
        queryClient.invalidateQueries({ queryKey: ["hotels"] }),
        queryClient.invalidateQueries({ queryKey: ["configuration-summary"] }),
      ]);
      setModalOpen(false);
      setHotel(empty);
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo guardar el hotel." }),
  });

  return (
    <ResourceShell resource="hotels">
      <ResourceTable
        title="Hoteles"
        data={hotels.data}
        search={list.search}
        setSearch={list.setSearch}
        apply={list.apply}
        onPage={list.setPage}
        createLabel="Nuevo hotel"
        getRowLabel={(item) => item.name}
        onCreate={() => {
          setHotel(empty);
          setModalOpen(true);
        }}
        onEdit={(item) => {
            setHotel({
              id: item.id,
              code: item.code,
              name: item.name,
              taxId: item.taxId ?? "",
              legalName: item.legalName,
              logo: item.logo,
              active: item.active,
            });
            setModalOpen(true);
          }}
        columns={[
          {
            key: "name",
            label: "Nombre",
            render: (item) => <strong>{item.name}</strong>,
          },
          {
            key: "code",
            label: "Código",
            className: "mono",
            render: (item) => item.code,
          },
          {
            key: "legal",
            label: "Razón social / CUIT",
            render: (item) => (
              <>
                <span>{item.legalName || "—"}</span>
                {item.taxId && <small className="mono">{item.taxId}</small>}
              </>
            ),
          },
          {
            key: "status",
            label: "Estado",
            render: (item) => <ResourceStatus active={item.active} />,
          },
        ]}
      />
      {modalOpen && (
        <SettingsCrudModal
          eyebrow="Configuración · Hoteles"
          title={hotel.id ? "Editar hotel" : "Crear hotel"}
          pending={save.isPending}
          onClose={() => setModalOpen(false)}
        >
          <form
            className="stack-form"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <div className="form-row">
              <label>
                Código
                <input
                  required
                  value={hotel.code}
                  onChange={(event) =>
                    setHotel({ ...hotel, code: event.target.value })
                  }
                />
              </label>
              <label>
                Nombre
                <input
                  required
                  value={hotel.name}
                  onChange={(event) =>
                    setHotel({ ...hotel, name: event.target.value })
                  }
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                CUIT
                <input
                  value={hotel.taxId}
                  onChange={(event) =>
                    setHotel({ ...hotel, taxId: event.target.value })
                  }
                />
              </label>
              <label>
                Razón social
                <input
                  value={hotel.legalName}
                  onChange={(event) =>
                    setHotel({ ...hotel, legalName: event.target.value })
                  }
                />
              </label>
            </div>
            <label>
              Logo en assets/
              <input
                value={hotel.logo}
                onChange={(event) =>
                  setHotel({ ...hotel, logo: event.target.value })
                }
              />
            </label>
            <ActiveField
              checked={hotel.active}
              onChange={(active) => setHotel({ ...hotel, active })}
              label="Hotel activo"
            />
            <FormActions
              pending={save.isPending}
              editing={Boolean(hotel.id)}
              noun="hotel"
              onCancel={() => setModalOpen(false)}
            />
          </form>
        </SettingsCrudModal>
      )}
    </ResourceShell>
  );
}

export function SettingsBanksPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const list = useListState();
  const empty = {
    id: "",
    code: "",
    name: "",
    country: "AR",
    active: true,
    accounts: [{ currency: "ARS" as const, active: true }, { currency: "USD" as const, active: false }],
  };
  const [bank, setBank] = useState(empty);
  const [modalOpen, setModalOpen] = useState(false);
  const banks = useQuery({
    queryKey: ["settings-banks", list.page, list.q],
    queryFn: (): Promise<PageResponse<Bank>> => responseData(apiClient.GET("/api/v1/banking/banks", {
      params: { query: { page: list.page, pageSize: 25, q: list.q, active: "all" } },
    })),
  });
  const save = useMutation({
    mutationFn: () => responseData(apiClient.POST("/api/v1/banking/banks", { body: bank })),
    onSuccess: async () => {
      toast.success("Banco guardado.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["settings-banks"] }),
        queryClient.invalidateQueries({ queryKey: ["bank-import-options"] }),
        queryClient.invalidateQueries({
          queryKey: ["reconciliation-master-options"],
        }),
        queryClient.invalidateQueries({ queryKey: ["configuration-summary"] }),
      ]);
      setModalOpen(false);
      setBank(empty);
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo guardar el banco." }),
  });

  return (
    <ResourceShell resource="banks">
      <ResourceTable
        title="Bancos"
        data={banks.data}
        search={list.search}
        setSearch={list.setSearch}
        apply={list.apply}
        onPage={list.setPage}
        createLabel="Nuevo banco"
        getRowLabel={(item) => item.name}
        onCreate={() => {
          setBank(empty);
          setModalOpen(true);
        }}
        onEdit={(item) => {
            setBank({
              id: item.id,
              code: item.code,
              name: item.name,
              country: item.country,
              active: item.active,
              accounts: item.accounts?.length ? item.accounts.map((account) => ({ currency: account.currency, active: account.active })) : [{ currency: "ARS", active: true }, { currency: "USD", active: false }],
            });
            setModalOpen(true);
          }}
        columns={[
          {
            key: "name",
            label: "Nombre",
            render: (item) => <strong>{item.name}</strong>,
          },
          {
            key: "code",
            label: "Código",
            className: "mono",
            render: (item) => item.code,
          },
          {
            key: "country",
            label: "País",
            className: "mono",
            render: (item) => item.country,
          },
          {
            key: "accounts",
            label: "Cuentas",
            render: (item) => <span>{(["ARS", "USD"] as const).map((currency) => `${currency}: ${item.accounts?.find((account) => account.currency === currency)?.active ? "Activa" : "Inactiva"}`).join(" · ")}</span>,
          },
          {
            key: "status",
            label: "Estado",
            render: (item) => <ResourceStatus active={item.active} />,
          },
        ]}
      />
      {modalOpen && (
        <SettingsCrudModal
          eyebrow="Configuración · Bancos"
          title={bank.id ? "Editar banco" : "Crear banco"}
          pending={save.isPending}
          onClose={() => setModalOpen(false)}
        >
          <form
            className="stack-form"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <div className="form-row">
              <label>
                Código
                <input
                  required
                  value={bank.code}
                  onChange={(event) =>
                    setBank({ ...bank, code: event.target.value })
                  }
                />
              </label>
              <label>
                País
                <input
                  required
                  value={bank.country}
                  onChange={(event) =>
                    setBank({ ...bank, country: event.target.value })
                  }
                />
              </label>
            </div>
            <label>
              Nombre
              <input
                required
                value={bank.name}
                onChange={(event) =>
                  setBank({ ...bank, name: event.target.value })
                }
              />
            </label>
            <ActiveField
              checked={bank.active}
              onChange={(active) => setBank({ ...bank, active })}
              label="Banco activo"
            />
            <fieldset className="settings-account-flags">
              <legend>Cuentas disponibles</legend>
              <ActiveField
                checked={bank.accounts.find((account) => account.currency === "ARS")?.active ?? false}
                onChange={(active) => setBank({ ...bank, accounts: bank.accounts.map((account) => account.currency === "ARS" ? { ...account, active } : account) })}
                label="Pesos (ARS)"
              />
              <ActiveField
                checked={bank.accounts.find((account) => account.currency === "USD")?.active ?? false}
                onChange={(active) => setBank({ ...bank, accounts: bank.accounts.map((account) => account.currency === "USD" ? { ...account, active } : account) })}
                label="Dólares (USD)"
              />
            </fieldset>
            <FormActions
              pending={save.isPending}
              editing={Boolean(bank.id)}
              noun="banco"
              onCancel={() => setModalOpen(false)}
            />
          </form>
        </SettingsCrudModal>
      )}
    </ResourceShell>
  );
}

type CardConfigurationItem = CardBrand;

function CardCatalogEditor() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const list = useListState();
  const definition = {
    title: "Marcas",
    noun: "marca",
    createLabel: "Nueva marca",
    savedMessage: "Marca guardada.",
    endpoint: "brands",
    activeLabel: "Marca activa",
  };
  const empty = {
    id: "",
    code: "",
    name: "",
    active: true,
  };
  const [current, setCurrent] = useState(empty);
  const [modalOpen, setModalOpen] = useState(false);
  const items = useQuery({
    queryKey: [`settings-${definition.endpoint}`, list.page, list.q],
    queryFn: (): Promise<PageResponse<CardConfigurationItem>> => responseData(apiClient.GET("/api/v1/cards/catalog/brands", {
      params: { query: { page: list.page, pageSize: 25, q: list.q, active: "all" } },
    })),
  });
  const save = useMutation({
    mutationFn: () => responseData(apiClient.POST("/api/v1/cards/catalog/brands", {
        body: {
          id: current.id,
          code: current.code,
          name: current.name,
          active: current.active,
        },
      })),
    onSuccess: async () => {
      toast.success(definition.savedMessage);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [`settings-${definition.endpoint}`],
        }),
        queryClient.invalidateQueries({
          queryKey: ["reconciliation-master-options"],
        }),
        queryClient.invalidateQueries({ queryKey: ["configuration-summary"] }),
      ]);
      setModalOpen(false);
      setCurrent(empty);
    },
    onError: (reason) => toast.error(reason, { fallback: `No se pudo guardar el ${definition.noun}.` }),
  });

  return (
    <>
      <ResourceTable
        title={definition.title}
        data={items.data}
        search={list.search}
        setSearch={list.setSearch}
        apply={list.apply}
        onPage={list.setPage}
        createLabel={definition.createLabel}
        getRowLabel={(item) => item.name}
        onCreate={() => {
          setCurrent(empty);
          setModalOpen(true);
        }}
        onEdit={(item) => {
          setCurrent({
            id: item.id,
            code: item.code,
            name: item.name,
            active: item.active,
          });
          setModalOpen(true);
        }}
        columns={[
          {
            key: "name",
            label: "Nombre",
            render: (item) => <strong>{item.name}</strong>,
          },
          {
            key: "code",
            label: "Código",
            className: "mono",
            render: (item) => item.code,
          },
          {
            key: "status",
            label: "Estado",
            render: (item) => <ResourceStatus active={item.active} />,
          },
        ]}
      />
      {modalOpen && (
        <SettingsCrudModal
          eyebrow={`Configuración · ${definition.title}`}
          title={current.id ? `Editar ${definition.noun}` : `Crear ${definition.noun}`}
          pending={save.isPending}
          onClose={() => setModalOpen(false)}
        >
        <form
          className="stack-form"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <div className="form-row">
            <label>
              Código
              <input
                required
                value={current.code}
                onChange={(event) =>
                  setCurrent({ ...current, code: event.target.value })
                }
              />
            </label>
            <label>
              Nombre
              <input
                required
                value={current.name}
                onChange={(event) =>
                  setCurrent({ ...current, name: event.target.value })
                }
              />
            </label>
          </div>
          <ActiveField
            checked={current.active}
            onChange={(active) => setCurrent({ ...current, active })}
            label={definition.activeLabel}
          />
          <FormActions
            pending={save.isPending}
            editing={Boolean(current.id)}
            noun={definition.noun}
            onCancel={() => setModalOpen(false)}
          />
        </form>
        </SettingsCrudModal>
      )}
    </>
  );
}

export function SettingsCardsPage() {
  return (
    <ResourceShell resource="cards">
      <CardCatalogEditor />
    </ResourceShell>
  );
}

export function SettingsAgenciesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const list = useListState();
  const empty = {
    id: "",
    code: "",
    name: "",
    agencyType: "OTA",
    aliases: "",
    veniceIds: "",
    reportProfile: "",
    active: true,
  };
  const [agency, setAgency] = useState(empty);
  const [modalOpen, setModalOpen] = useState(false);
  const agencies = useQuery({
    queryKey: ["settings-agencies", list.page, list.q],
    queryFn: (): Promise<PageResponse<Agency>> => responseData(apiClient.GET("/api/v1/ota/agencies", {
      params: { query: { page: list.page, pageSize: 25, q: list.q, active: "all" } },
    })),
  });
  const save = useMutation({
    mutationFn: () => responseData(apiClient.POST("/api/v1/ota/agencies", {
        body: {
          ...agency,
          aliases: agency.aliases
            .split(",")
            .map((alias) => alias.trim())
            .filter(Boolean),
          veniceIds: agency.veniceIds
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean),
        },
      })),
    onSuccess: async () => {
      toast.success("Agencia guardada.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["settings-agencies"] }),
        queryClient.invalidateQueries({
          queryKey: ["reconciliation-master-options"],
        }),
        queryClient.invalidateQueries({ queryKey: ["configuration-summary"] }),
      ]);
      setModalOpen(false);
      setAgency(empty);
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo guardar la agencia." }),
  });

  return (
    <ResourceShell resource="agencies">
      <ResourceTable
        title="Agencias"
        data={agencies.data}
        search={list.search}
        setSearch={list.setSearch}
        apply={list.apply}
        onPage={list.setPage}
        createLabel="Nueva agencia"
        getRowLabel={(item) => item.name}
        onCreate={() => {
          setAgency(empty);
          setModalOpen(true);
        }}
        onEdit={(item) => {
            setAgency({
              id: item.id,
              code: item.code,
              name: item.name,
              agencyType: item.agencyType,
              aliases: item.aliases?.join(", ") ?? "",
              veniceIds: item.veniceIds?.join(", ") ?? "",
              reportProfile: item.reportProfile ?? "",
              active: item.active,
            });
            setModalOpen(true);
          }}
        columns={[
          {
            key: "name",
            label: "Nombre",
            render: (item) => <strong>{item.name}</strong>,
          },
          {
            key: "code",
            label: "Código",
            className: "mono",
            render: (item) => item.code,
          },
          {
            key: "type",
            label: "Tipo",
            render: (item) => item.agencyType,
          },
          {
            key: "profile",
            label: "Perfil",
            render: (item) => item.reportProfile || "—",
          },
          {
            key: "status",
            label: "Estado",
            render: (item) => <ResourceStatus active={item.active} feminine />,
          },
        ]}
      />
      {modalOpen && (
        <SettingsCrudModal
          eyebrow="Configuración · Agencias"
          title={agency.id ? "Editar agencia" : "Crear agencia"}
          pending={save.isPending}
          onClose={() => setModalOpen(false)}
        >
          <form
            className="stack-form"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <div className="form-row">
              <label>
                Código
                <input
                  required
                  value={agency.code}
                  onChange={(event) =>
                    setAgency({ ...agency, code: event.target.value })
                  }
                />
              </label>
              <label>
                Tipo
                <input
                  required
                  value={agency.agencyType}
                  onChange={(event) =>
                    setAgency({ ...agency, agencyType: event.target.value })
                  }
                />
              </label>
            </div>
            <label>
              Nombre
              <input
                required
                value={agency.name}
                onChange={(event) =>
                  setAgency({ ...agency, name: event.target.value })
                }
              />
            </label>
            <label>
              Aliases separados por coma
              <textarea
                rows={4}
                value={agency.aliases}
                onChange={(event) =>
                  setAgency({ ...agency, aliases: event.target.value })
                }
              />
            </label>
            <label>
              IDs de agencia en Venice
              <textarea
                aria-label="IDs de agencia en Venice"
                rows={2}
                value={agency.veniceIds}
                onChange={(event) =>
                  setAgency({ ...agency, veniceIds: event.target.value })
                }
                placeholder="Separados por coma"
              />
              <small>
                Se aprenden automáticamente al sincronizar y también pueden
                corregirse manualmente.
              </small>
            </label>
            <label>
              Perfil del reporte de cobranzas
              <select
                value={agency.reportProfile}
                onChange={(event) =>
                  setAgency({ ...agency, reportProfile: event.target.value })
                }
              >
                <option value="">Sin perfil específico</option>
                <option value="AZUL">Azul Viagems</option>
                <option value="BUQUEBUS">Buquebus</option>
                <option value="CVC">CVC</option>
                <option value="TRAVCO">Travco</option>
                <option value="DCOM">Dcom</option>
                <option value="ORINTER">Orinter</option>
                <option value="INCOMUN">Incomun</option>
                <option value="OTS">OTS</option>
              </select>
            </label>
            <ActiveField
              checked={agency.active}
              onChange={(active) => setAgency({ ...agency, active })}
              label="Agencia activa"
            />
            <small>
              Sólo las agencias activas ingresan a OtaPage y reportes de
              cobranzas. Para recuperar reservas al reactivar,
              resincronizá el rango correspondiente.
            </small>
            <FormActions
              pending={save.isPending}
              editing={Boolean(agency.id)}
              noun="agencia"
              onCancel={() => setModalOpen(false)}
            />
          </form>
        </SettingsCrudModal>
      )}
    </ResourceShell>
  );
}
